/**
 * The ONE Tasks chip (The Quiet Toolbar, 10/10 —
 * https://claude.ai/artifact/867xubbA5qmtEe3s5KsP2c "What each chip shows").
 *
 * Before, "tasks on you" was printed three times (the Tasks door, the Tasks
 * tab badge, "Needs you · N") and what came back to you was a fourth light
 * ("N back"). With the quiet toolbar on, all four are this one chip:
 *
 *   - ONE number = on you + came back to you (Corbin's pick, 10/10). Both come
 *     from GET /api/tasks/counts (`needsYou`, `backToYou`), the same server
 *     numbers the Tasks page reads, so the chip and the page cannot disagree.
 *   - Gray at 0, amber at 1 or more. A failed read shows "—", never a 0.
 *   - Hover (or tap) lists "Came back to you" FIRST, then "On you".
 *
 * Tasks only — never email (that is the Inbox chip) and never job problems
 * (that is Stuck). Each fact is counted in exactly one place.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, ClipboardList, CornerUpLeft, X } from "lucide-react";
import { api } from "@/lib/api";
import { useWorkCounts, type WorkCounts } from "@/lib/counts";
import { openTaskHere } from "@/lib/task-flyout";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { maskMoney } from "@/lib/mask-money";
import { TaskRecordChip } from "@/components/task-record-chip";
import { firstName, type BackItem, type InboxItem, type InboxResponse } from "@/lib/tasks";

/** What the chip says. Pure, so the one-number rule is pinned by a test. */
export function tasksChipState(
    counts: Pick<WorkCounts, "needsYou" | "backToYou"> | undefined,
    isError: boolean,
): { n: number | null; hot: boolean; label: string } {
    if (isError) {
        return { n: null, hot: false, label: "Tasks — couldn't load your count" };
    }
    const onYou = counts?.needsYou ?? 0;
    const back = counts?.backToYou ?? 0;
    const n = onYou + back;
    const parts = [back > 0 ? `${back} came back to you` : null, onYou > 0 ? `${onYou} on you` : null].filter(Boolean);
    return { n, hot: n > 0, label: n > 0 ? `Tasks — ${parts.join(", ")}` : "Tasks — nothing on you" };
}

/**
 * The ONE Tasks button, for everyone (Tasks Both Ways, pick 5A, Corbin 10/28:
 * "one Tasks button for everyone"). The old toolbar's separate Tasks button
 * with only the Needs you count, the Tasks tab badge and the green "N back"
 * chip are gone; this chip is the only place a task number shows. A new
 * task, a reply and an @mention never ring the bell: the new task is on this
 * number, and the reply sits on the task itself.
 */
export function TasksChip({ onNavigate }: { onNavigate?: () => void }) {
    const navigate = useNavigate();
    const { can } = useAuth();
    // Task titles can carry a dollar figure; people who can't see money get
    // "$•••" (kept from the old "Needs you" list, 10/16).
    const canSeeMoney = can(PERMISSIONS.financials.view) || can(PERMISSIONS.finance.view);
    const work = useWorkCounts();
    const { n, hot, label } = tasksChipState(work.counts, work.isError);
    const [open, setOpen] = useState(false);
    const onYou = useQuery({
        queryKey: ["tasks", "my-inbox"],
        queryFn: () => api.get<InboxResponse>("/api/tasks/my-inbox"),
        enabled: open,
    }).data;
    const back = useQuery({
        queryKey: ["tasks", "back-to-me"],
        queryFn: () => api.get<{ items: BackItem[] }>("/api/tasks/back-to-me"),
        enabled: open,
    }).data;
    const backItems = back?.items ?? [];
    const onYouItems: InboxItem[] = (onYou?.items ?? []).filter((t) => !t.claimable);

    const wrapRef = useRef<HTMLDivElement>(null);
    const popRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (wrapRef.current?.contains(t) || popRef.current?.contains(t)) return;
            setOpen(false);
        };
        const place = () => {
            const r = btnRef.current?.getBoundingClientRect();
            if (r) setAnchor({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
        };
        place();
        window.addEventListener("resize", place);
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
            window.removeEventListener("resize", place);
        };
    }, [open]);

    const go = (href: string) => {
        setOpen(false);
        onNavigate?.();
        navigate(href);
    };
    // A task row opens the task over the page you are on (lib/task-flyout.ts).
    const openTask = (href: string) => {
        setOpen(false);
        openTaskHere(href, (to) => {
            onNavigate?.();
            navigate(to);
        });
    };
    const hoverable = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches;
    const enter = () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        if (hoverable()) hoverTimer.current = setTimeout(() => setOpen(true), 150);
    };
    const leave = () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        if (hoverable()) hoverTimer.current = setTimeout(() => setOpen(false), 300);
    };

    return (
        <div ref={wrapRef} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
            <button
                ref={btnRef}
                type="button"
                data-testid="tasks-chip"
                data-teach="tasks"
                data-hot={hot ? "true" : "false"}
                className={
                    hot
                        ? "door !border-amber-600 !bg-amber-600 !text-white hover:!bg-amber-700 dark:!border-amber-500 dark:!bg-amber-500 dark:!text-stone-950"
                        : "door !text-muted-foreground"
                }
                aria-label={label}
                aria-expanded={open}
                aria-haspopup="dialog"
                onClick={() => setOpen((v) => !v)}
            >
                <ClipboardList size={15} />
                Tasks
                {n === null ? (
                    <span aria-hidden>—</span>
                ) : (
                    hot && (
                        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-2 text-sm font-extrabold tabular-nums text-amber-800 dark:bg-stone-950 dark:text-amber-300">
                            {n}
                        </span>
                    )
                )}
            </button>

            {open &&
                createPortal(
                    <div
                        ref={popRef}
                        role="dialog"
                        aria-label="Your tasks"
                        data-testid="tasks-preview"
                        onMouseEnter={enter}
                        onMouseLeave={leave}
                        style={
                            anchor && typeof window !== "undefined" && window.innerWidth >= 640
                                ? { top: anchor.top, right: anchor.right }
                                : undefined
                        }
                        className="fixed inset-x-2 top-16 z-[60] overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-2xl sm:inset-x-auto sm:w-[420px]"
                    >
                        <div className="flex items-baseline justify-between gap-2 px-4 pb-2 pt-3">
                            <b className="text-base font-extrabold">
                                {n ? `${n} task${n === 1 ? "" : "s"} for you` : "Nothing on you"}
                            </b>
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setOpen(false)}
                                className="rounded p-1 hover:bg-muted sm:hidden"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto">
                            {backItems.length > 0 && (
                                <section data-testid="tasks-preview-back">
                                    <h4 className="px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Came back to you · {backItems.length}
                                    </h4>
                                    <ul>
                                        {backItems.slice(0, 5).map((b) => (
                                            <li
                                                key={b.id}
                                                className="grid grid-cols-[34px_1fr_auto] items-center gap-2.5 border-t border-border px-4 py-2"
                                            >
                                                <span className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                                                    <CornerUpLeft className="h-4 w-4" />
                                                </span>
                                                <button
                                                    type="button"
                                                    className="min-w-0 text-left"
                                                    onClick={() => openTask(b.href)}
                                                >
                                                    <span className="block truncate text-sm font-semibold">
                                                        {maskMoney(b.title, canSeeMoney)}
                                                    </span>
                                                    <span className="block truncate text-xs text-muted-foreground">
                                                        {firstName(b.byName)}
                                                        {b.kind === "cant_do"
                                                            ? " can't do it"
                                                            : b.kind === "handed_back"
                                                              ? " handed it back"
                                                              : " passed it back"}
                                                        {b.note ? `: “${b.note}”` : ""}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openTask(b.href)}
                                                    className="whitespace-nowrap rounded-lg bg-foreground px-3 py-1.5 text-xs font-bold text-background hover:opacity-90"
                                                >
                                                    {b.kind === "handed_back" ? "Confirm" : "Decide"}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                            <section data-testid="tasks-preview-on-you">
                                <h4 className="px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                                    On you · {work.counts.needsYou}
                                </h4>
                                {onYouItems.length === 0 ? (
                                    <div className="flex items-center gap-2 border-t border-border px-4 py-4 text-sm text-muted-foreground">
                                        <Check className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
                                        Nothing waiting on you.
                                    </div>
                                ) : (
                                    <ul>
                                        {onYouItems.slice(0, 5).map((t) => (
                                            <li key={t.id} className="border-t border-border">
                                                <button
                                                    type="button"
                                                    onClick={() => openTask(t.href ?? "/tasks")}
                                                    className="grid w-full grid-cols-[34px_1fr] items-center gap-2.5 px-4 py-2 text-left hover:bg-muted/60"
                                                >
                                                    <span className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-secondary text-foreground">
                                                        <ClipboardList className="h-4 w-4" />
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block truncate text-sm font-semibold">
                                                            {maskMoney(t.title, canSeeMoney)}
                                                        </span>
                                                        <span className="block truncate text-xs text-muted-foreground">
                                                            {t.priority === "urgent" ? "Urgent · " : ""}
                                                            {t.dueDate ? `Due ${t.dueDate}` : (t.typeName ?? "On you")}
                                                        </span>
                                                    </span>
                                                </button>
                                                {t.record && (
                                                    <div className="flex px-4 pb-2 pl-[60px]">
                                                        <TaskRecordChip record={t.record} />
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        </div>
                        <div className="flex justify-end bg-muted/50 px-4 py-2.5 text-xs">
                            <button
                                type="button"
                                onClick={() => go("/tasks")}
                                className="font-bold text-[#7A5A0A] hover:underline dark:text-amber-300"
                            >
                                See all in Tasks →
                            </button>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
